import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, type ReactNode } from "react";

import { ApiError } from "@/lib/api";
import { getMe, logout, meQueryKey } from "@/lib/auth-api";
import { serviceProblem } from "@/lib/service-problems";

import { AuthContext, type AuthStatus } from "./context";

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const me = useQuery({
    queryKey: meQueryKey,
    queryFn: getMe,
    retry: false,
    staleTime: 60_000,
    // While the database or the server is down, check again periodically.
    refetchInterval: (query) => (serviceProblem(query.state.error) ? 10_000 : false),
  });
  const problem = me.data ? null : serviceProblem(me.error);

  let status: AuthStatus = "loading";
  if (me.data) status = me.data.state === "active" ? "active" : "step";
  else if (problem) status = "unavailable";
  else if (me.error instanceof ApiError && me.error.status === 401) status = "anonymous";
  else if (me.isError) status = "error";

  const permissions = useMemo(() => new Set(me.data?.permissions ?? []), [me.data]);
  const can = useCallback((permission: string) => permissions.has(permission), [permissions]);
  const canAny = useCallback((...codes: string[]) => codes.some((c) => permissions.has(c)), [permissions]);
  const refresh = useCallback(() => me.refetch(), [me]);
  const signOut = useCallback(async () => {
    try {
      await logout();
    } finally {
      // Forget everything fetched for this user, then ask the server again who we are (now: nobody).
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== meQueryKey[0] });
      await queryClient.resetQueries({ queryKey: meQueryKey });
    }
  }, [queryClient]);

  const value = useMemo(
    () => ({ status, me: me.data ?? null, problem, can, canAny, refresh, signOut }),
    [status, me.data, problem, can, canAny, refresh, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
